import {
  BASE_PROMPT,
  CATEGORIES as RAW_CATEGORIES,
  GENDERS as RAW_GENDERS,
  CHARACTERS as RAW_CHARACTERS,
  SURPRISE_ONLY_CHARACTERS as RAW_SURPRISE_ONLY,
} from './charactersData';

// Base paths for local demo images copied to public/demos
const IMAGE_BASE_PATH = '/demos';
const GIRLS_IMAGE_BASE_PATH = '/demos/girls';

// When a character has no dedicated image, map to the closest existing image
const FALLBACK_IMAGE_MAP = {
  // Hero characters whose files have non-standard names
  blackwidow: 'black_widow_fixed.jpg',
  captainmarvel: 'captain_marvel_fixed.jpg',
  cyberpunk: 'cyberpunk-hacker.jpg',
  // male variants fallback to base
  photographer_male: 'photographer-.jpg',
  cyberpunk_soldier: 'cyberpunk-hacker.jpg',
  renaissance_painter: 'renaissance.jpg',
  medieval_queen: 'medieval-king.jpg',
  safari_guide: 'safari.jpg',
  formula_1_driver: 'racer.jpg',
  ballet_dancer: 'dancer.jpg',
  street_magician: 'magician.jpg',
  poker_player: 'spy.jpg',
  master_chocolatier: 'chocolatier.jpg',
  miner: 'construction_worker.jpg',
  ski_instructor: 'mountaineer.jpg',
  martial_arts_master: 'samurai.jpg',
  cyborg_assassin: 'cyborg.jpg',
  high_elf_archer: 'elf.jpg',
  dwarf_warrior: 'dwarf.jpg',
  orc_chieftain: 'barbarian.jpg',
  greek_god: 'greek-deity.jpg',
  detective_modern: 'detective.jpg',
  pop_star: 'rockstar.jpg',
  movie_director: 'actor.jpg',
  sculptor: 'artist.jpg',
  surgeon_male: 'doctor.jpg',
  nurse_male: 'doctor.jpg',
  pastry_chef_male: 'chef.jpg',
  paramedic_male: 'pramedic.jpg',
  police_officer_male: 'police_officer.jpg',
  navy_seal: 'navy_seal_v2.jpg',
  tennis_player: 'tennis.jpg',
  football_player: 'football.jpg',
  baseball_player: 'baseball.jpg',
  sorcerer: 'wizard.jpg',
  elf_male: 'elf.jpg',
  spartan: 'gladiator.jpg',
  roman_soldier: 'roman.jpg',
  native_american_chief: 'native_american_chief_gen.jpg',
  popstar: 'rockstar.jpg',
  painter: 'artist.jpg',
  photographer: 'photographer-.jpg',
  robot_engineer: 'android.jpg',
  werewolf_human: 'werewolf.jpg',
  superhero_custom: 'superman.jpg',
  business_professional: 'businessman.jpg',
  fashion_model: 'streetwear-model.jpg',
  athlete_general: 'runner.jpg',
  // female fallbacks (resolved inside /demos/girls)
  surgeon_female: 'doctor_female.jpg',
  nurse_female: 'doctor_female.jpg',
  paramedic_female: 'doctor_female.jpg',
  pastry_chef_female: 'chef_female.jpg',
  chef_female: 'chef_female.jpg',
  fighter_pilot_female: 'fighter_pilot_female.jpg',
  astronaut_female: 'astronaut_female.jpg',
  fashion_influencer_female: 'fashion_influencer_female.jpg',
  beauty_vlogger_female: 'beauty_vlogger_female.jpg',
  coffee_artist_female: 'coffee_artist_female.jpg',
  travel_blogger_female: 'travel_blogger_female.jpg',
  yoga_instructor_female: 'yoga-instructor.jpg',
  police_officer_female: 'police_female.jpg',
  cleopatra: 'queen.jpg', // no Egyptian queen image yet (/demos/cleopatra.jpg shows a man)
  // Female characters whose image sits in /demos itself (leading '/')
  veterinarian_female: '/veterinarian_female.jpg',
  geisha: '/geisha_gen.jpg',
  // Male characters that need the girls dir image
  fairy: 'girls/fairy_female.jpg',
  // Characters whose actual filename doesn't match ID pattern
  fighter_pilot: 'fighter_pilot_fixed.jpg',
  construction_worker: 'construction_worker.jpg',
};

function buildLocalImageUrl(char) {
  if (!char?.id) return char.image_url;

  const id = String(char.id);

  // Check hardcoded fallback first
  if (FALLBACK_IMAGE_MAP[id]) {
    const base = FALLBACK_IMAGE_MAP[id];
    // A leading '/' pins the file to /demos itself, whatever the gender
    if (base.startsWith('/')) {
      return `${IMAGE_BASE_PATH}${base}`;
    }
    // Support 'girls/' prefix for characters that use the girls subfolder image
    if (base.startsWith('girls/')) {
      return `${IMAGE_BASE_PATH}/${base}`;
    }
    const gender = GENDER_BY_DEMO_ID.get(id) || char.gender;
    if (gender === 'female') {
      return `${GIRLS_IMAGE_BASE_PATH}/${base}`;
    }
    return `${IMAGE_BASE_PATH}/${base}`;
  }

  const gender = GENDER_BY_DEMO_ID.get(id) || char.gender;

  const candidates = [];

  const add = (name) => {
    if (name && !candidates.includes(name)) {
      candidates.push(name);
    }
  };

  const withHyphens = id.replace(/_/g, '-');

  if (/_male$/.test(id)) {
    const base = id.replace(/_male$/, '');
    const baseHyphen = base.replace(/_/g, '-');
    // Male variants usually share the same base filename as the neutral version
    add(`${baseHyphen}.jpg`);
    add(`${base}.jpg`);
    // Fallbacks with explicit male suffix
    add(`${withHyphens}.jpg`);
    add(`${id}.jpg`);
  } else if (/_female$/.test(id)) {
    const base = id.replace(/_female$/, '');
    const baseHyphen = base.replace(/_/g, '-');
    // Prefer hyphenated female filenames like greek-deity_female.jpg
    add(`${baseHyphen}_female.jpg`);
    // Then plain id-based name
    add(`${id}.jpg`);
    add(`${base}_female.jpg`);
    // Finally, try base forms
    add(`${baseHyphen}.jpg`);
    add(`${base}.jpg`);
    add(`${withHyphens}.jpg`);
  } else {
    // Non-suffixed ids often use hyphens in the filename (alien-diplomat, time-traveler, etc.)
    add(`${withHyphens}.jpg`);
    add(`${id}.jpg`);
  }

  let paths = [];
  if (gender === 'female') {
    paths = [
      ...candidates.map((n) => `${GIRLS_IMAGE_BASE_PATH}/${n}`),
      ...candidates.map((n) => `${IMAGE_BASE_PATH}/${n}`),
    ];
  } else {
    paths = candidates.map((n) => `${IMAGE_BASE_PATH}/${n}`);
  }

  return paths[0] || char.image_url;
}


// All demo image ids (derived from filenames) for male characters in public/demos
const DEMOS_IDS_MALE = [
  'superman',
  'batman',
  'wonderwoman',
  'spiderman',
  'ironman',
  'blackwidow',
  'captainmarvel',
  'doctor',
  'chef',
  'pilot',
  'astronaut',
  'firefighter',
  'police',
  'judge',
  'soldier',
  'fighter_pilot',
  'soccer',
  'basketball',
  'boxer',
  'runner',
  'skater',
  'swimmer',
  'surfer',
  'tennis',
  'diver',
  'wizard',
  'knight',
  'pirate',
  'ninja',
  'viking',
  'elf',
  'fairy',
  'pharaoh',
  'samurai',
  'cowboy',
  'ceo',
  'accountant',
  'rockstar',
  'actor',
  'dj',
  'spy',
  'biker',
  'android',
  'alien_diplomat',
  'jedi',
  'explorer',
  'archaeologist',
  'angel',
  'aquaman',
  'tech_bro_male',
  'esports_gamer_male',
  'streetwear_model_male',
  'fitness_influencer_male',
  'coffee_barista_male',
  'photographer_male',
  'mechanic_male',
  'firefighter_male',
  'noir_detective_male',
  'tech_billionaire_male',
  'greek_philosopher_male',
  'cyberpunk_racer_male',
  'forest_ranger_male',
  'royal_aristocrat_male',
  'sushi_chef_male',
  'olympic_fencer_male',
  'steampunk_inventor_male',
  'western_outlaw_male',
  'fire_elemental_knight',
  'cyber_neon_shinobi',
  'frost_king',
  'golden_statue',
  'urban_sorcerer',
  'stardust_explorer',
  'wasteland_survivor',
  'atlantean_prince',
  'clockwork_automaton',
  'forest_guardian_spirit',
  'cyberpunk_soldier',
  'renaissance_painter',
  'medieval_queen',
  'medieval_king',
  'safari_guide',
  'formula_1_driver',
  'ballet_dancer',
  'street_magician',
  'poker_player',
  'master_chocolatier',
  'gardener',
  'construction_worker',
  'miner',
  'ski_instructor',
  'martial_arts_master',
  'cyborg_assassin',
  'high_elf_archer',
  'dwarf_warrior',
  'orc_chieftain',
  'greek_god',
  'gladiator',
  'detective_modern',
  'pop_star',
  'movie_director',
  'sculptor',
  'doctor_male',
  'surgeon_male',
  'nurse_male',
  'chef_male',
  'pastry_chef_male',
  'pilot_male',
  'paramedic_male',
  'police_officer_male',
  'navy_seal',
  'marine',
  'tennis_player',
  'football_player',
  'baseball_player',
  'sorcerer',
  'elf_male',
  'spartan',
  'roman_soldier',
  'native_american_chief',
  'lawyer',
  'scientist',
  'teacher',
  'architect',
  'popstar',
  'dancer',
  'painter',
  'photographer',
  'mountaineer',
  'cyberpunk',
  'space_marine',
  'robot_engineer',
  'time_traveler',
  'vampire',
  'werewolf_human',
  'superhero_custom',
  'monk',
  'business_professional',
  'fashion_model',
  'athlete_general',
];

// All demo image ids (derived from filenames) for female characters in public/demos/girls
const DEMOS_IDS_FEMALE = [
  'soldier_female',
  'fashion_influencer_female',
  'yoga_instructor_female',
  'beauty_vlogger_female',
  'coffee_artist_female',
  'travel_blogger_female',
  'lawyer_female',
  'scientist_female',
  'architect_female',
  'veterinarian_female',
  'doctor_female',
  'surgeon_female',
  'nurse_female',
  'chef_female',
  'pastry_chef_female',
  'pilot_female',
  'fighter_pilot_female',
  'astronaut_female',
  'firefighter_female',
  'paramedic_female',
  'police_officer_female',
  'soccer_female',
  'basketball_female',
  'boxer_female',
  'witch',
  'elf_female',
  'knight_female',
  'samurai_female',
  'viking_female',
  'pirate_female',
  'cleopatra',
  'cowgirl',
  'geisha',
  'ceo_female',
  'rockstar_female',
];

function inferCategoryFromId(id) {
  const key = String(id).toLowerCase();

  if (/(soccer|football|basketball|baseball|tennis|golfer|runner|skater|surfer|swimmer|cyclist|athlete|racer)/.test(key)) {
    return 'sports';
  }

  if (/(soldier|marine|navy|paratrooper|pilot_military|special_forces|royal_guard|space_marine)/.test(key)) {
    return 'military';
  }

  if (/(doctor|chef|teacher|architect|engineer|gardener|mechanic|scientist|judge|librarian)/.test(key)) {
    return 'professions';
  }

  if (/(accountant|banker|ceo|businessman|businesswoman|lawyer|entrepreneur)/.test(key)) {
    return 'business';
  }

  if (/(actor|actress|rockstar|singer|^dj$|_dj|comedian|circus_performer|magician)/.test(key)) {
    return 'entertainment';
  }

  if (/(superman|batman|batwoman|wonderwoman|ironman|ironwoman|hulk|she_hulk|spiderman|spiderwoman|thor|lady_thor|aquaman|captain_marvel|captainmarvel|blackwidow|black_widow|superheroine|superwoman|flash)/.test(key)) {
    return 'heroes';
  }

  if (/(wizard|witch|elf|dwarf|barbarian|sorceress|fairy|demon|vampire|werewolf|deity|mermaid|paladin|angel)/.test(key)) {
    return 'fantasy';
  }

  if (/(pharaoh|cleopatra|roman|medieval|empress|queen|viking|napoleon|samurai|renaissance)/.test(key)) {
    return 'historical';
  }

  if (/(android|cyborg|alien|jedi|time_traveler)/.test(key)) {
    return 'scifi';
  }

  if (/(explorer|archaeologist|treasure_hunter|mountaineer|safari|cowboy|cowgirl)/.test(key)) {
    return 'adventure';
  }

  return 'arts';
}

const DEMOS_IDS_NEUTRAL = [
  'caricature',
  'comics'
];

const DEMOS_CHARACTERS = [
  ...DEMOS_IDS_MALE.map((id) => ({ id, gender: 'male', category: inferCategoryFromId(id) })),
  ...DEMOS_IDS_FEMALE.map((id) => ({ id, gender: 'female', category: inferCategoryFromId(id) })),
  ...DEMOS_IDS_NEUTRAL.map((id) => ({ id, gender: 'all', category: inferCategoryFromId(id) })),
];

const GENDER_BY_DEMO_ID = new Map([
  ...DEMOS_IDS_MALE.map((id) => [id, 'male']),
  ...DEMOS_IDS_FEMALE.map((id) => [id, 'female']),
  ...DEMOS_IDS_NEUTRAL.map((id) => [id, 'all']),
]);

// Hebrew display names for demo-based characters that don't have explicit Hebrew names
const HEBREW_NAME_OVERRIDES = {
  caricature: 'קריקטורה',
  comics: 'קומיקס',
  // Male
  accountant: 'רואה חשבון',
  actor: 'שחקן',
  alien_diplomat: 'דיפלומט חייזרי',
  android: 'אנדרואיד',
  angel: 'מלאך',
  aquaman: 'אקווהמן',
  archaeologist: 'ארכיאולוג',
  archer: 'קשת',
  architect: 'אדריכל',
  artist: 'אמן',
  astronaut: 'אסטרונאוט',
  banker: 'בנקאי',
  barbarian: 'ברברי',
  baseball: 'שחקן בייסבול',
  basketball: 'שחקן כדורסל',
  batman: 'באטמן',
  batwoman: 'באטוומן',
  biker: 'רוכב אופנוע',
  boxer: 'מתאגרף',
  businessman: 'איש עסקים',
  captain: 'קפטן',
  ceo: 'מנכ"ל',
  chef: 'שף',
  circus_performer: 'אמן קרקס',
  cleopatra: 'קליאופטרה',
  comedian: 'קומיקאי',
  commander: 'מפקד',
  conductor: 'מנצח',
  cowboy: 'קאובוי',
  cyberpunk_hacker: 'האקר סייברפאנק',
  cyborg: 'סייבורג',
  cyclist: 'רוכב אופניים',
  dancer: 'רקדן',
  demon: 'שד',
  detective: 'בלש',
  diver: 'צוללן',
  dj: 'די.ג׳יי',
  doctor: 'רופא',
  dwarf: 'גמד',
  elf: 'אלף',
  engineer: 'מהנדס',
  entrepreneur: 'יזם',
  explorer: 'חוקר',
  firefighter: 'כבאי',
  flash: 'פלאש',
  football: 'שחקן פוטבול',
  forest_ranger: 'סייר יערות',
  gardener: 'גנן',
  general: 'גנרל',
  gladiator: 'גלדיאטור',
  golfer: 'שחקן גולף',
  greek_deity: 'אל יווני',
  greek_philosopher: 'פילוסוף יווני',
  hulk: 'האלק',
  ironman: 'איירון מן',
  jedi: 'ג׳דיי',
  knight: 'אביר',
  lawyer: 'עו"ד',
  librarian: 'ספרן',
  magician: 'קוסם',
  marine: 'נחת',
  mechanic: 'מכונאי',
  medieval_king: 'מלך מימי הביניים',
  monk: 'נזיר',
  mountaineer: 'מטפס הרים',
  napoleon: 'נפוליאון',
  navy: 'קצין צי',
  ninja: 'נינג׳ה',
  noir_detective: 'בלש נואר',
  olympic_fencer: 'סייף אולימפי',
  paladin: 'פלאדין',
  paratrooper: 'צנחן',
  pharaoh: 'פרעה',
  pilot_military: 'טייס צבאי',
  pilot: 'טייס',
  pirate: 'פיראט',
  police: 'שוטר',
  post_apocalyptic: 'ניצול פוסט-אפוקליפטי',
  racer: 'נהג מרוצים',
  renaissance: 'דמות רנסנס',
  rockstar: 'רוקסטאר',
  roman: 'לוחם רומי',
  ronin: 'רונין',
  royal_aristocrat: 'אציל מהמאה ה-18',
  royal_guard: 'שומר מלכותי',
  runner: 'רץ',
  safari: 'מדריך ספארי',
  samurai: 'סמוראי',
  scientist: 'מדען',
  singer: 'זמר',
  skater: 'סקייטר',
  soccer: 'כוכב כדורגל',
  soldier: 'חייל',
  sorceress: 'מכשפה',
  space_marine: 'לוחם חלל',
  special_forces: 'לוחם יחידה מיוחדת',
  spiderman: 'ספיידרמן',
  spy: 'מרגל',
  steampunk_inventor: 'ממציא סטימפאנק',
  superman: 'סופרמן',
  surfer: 'גולש גלים',
  sushi_chef: 'שף סושי',
  swimmer: 'שחיין',
  teacher: 'מורה',
  tech_billionaire: 'טייקון הייטק',
  tennis: 'שחקן טניס',
  thor: 'ת׳ור',
  time_traveler: 'נוסע בזמן',
  treasure_hunter: 'צייד אוצרות',
  vampire: 'ערפד',
  viking_king: 'מלך ויקינגי',
  viking: 'ויקינג',
  western_outlaw: 'קאובוי בצהרי היום',
  fire_elemental_knight: 'אל האש',
  cyber_neon_shinobi: 'סייבר-נינג\'ה',
  frost_king: 'מלך הקרח',
  golden_statue: 'פסל זהב חי',
  urban_sorcerer: 'קוסם מודרני',
  stardust_explorer: 'חלוץ חלל גלקטי',
  wasteland_survivor: 'לוחם אפוקליפטי',
  atlantean_prince: 'נסיך המצולות',
  clockwork_automaton: 'מאסטר השעונים',
  forest_guardian_spirit: 'אל יער אגדי',
  werewolf: 'איש זאב',
  wizard: 'קוסם',
  wonderwoman: 'וונדר וומן',

  // Female
  accountant_female: 'רואת חשבון',
  actress: 'שחקנית',
  alien_female: 'חייזרית',
  android_female: 'אנדרואידית',
  angel_female: 'מלאכית',
  architect_female: 'אדריכלית',
  artist_female: 'אמנית',
  astronaut_female: 'אסטרונאוטית',
  athlete_female: 'אתלטית',
  banker_female: 'בנקאית',
  barbarian_female: 'ברברית',
  baseball_female: 'שחקנית בייסבול',
  basketball_female: 'שחקנית כדורסל',
  biker_female: 'רוכבת אופנוע',
  boxer_female: 'מתאגרפת',
  businesswoman: 'אשת עסקים',
  captain_marvel: 'קפטן מארוול',
  ceo_female: 'מנכ"לית',
  chef_female: 'שפית',
  circus_performer_female: 'אמנית קרקס',
  comedian_female: 'קומיקאית',
  cowgirl: 'קאוגירל',
  cyberpunk_hacker_female: 'האקרית סייברפאנק',
  cyborg_female: 'סייבורגית',
  cyclist_female: 'רוכבת אופניים',
  dancer_female: 'רקדנית',
  demon_female: 'שדה',
  detective_female: 'בלשית',
  dj_female: 'די.ג׳ייית',
  doctor_female: 'רופאה',
  dwarf_female: 'גמדה',
  elf_female: 'אלפית',
  empress: 'קיסרית',
  engineer_female: 'מהנדסת',
  explorer_female: 'חוקרת',
  fairy_female: 'פיית',
  firefighter_female: 'כבאית',
  flash_female: 'פלאש (אישה)',
  football_female: 'שחקנית פוטבול',
  gardener_female: 'גננת גנים',
  gladiator_female: 'גלדיאטורית',
  golfer_female: 'שחקנית גולף',
  greek_deity_female: 'אלה יוונית',
  ironwoman: 'איירון וומן',
  jedi_female: 'ג׳דייית',
  judge_female: 'שופטת',
  knight_female: 'אבירה',
  lady_thor: 'ליידי ת׳ור',
  lawyer_female: 'עורכת דין',
  librarian_female: 'ספרנית',
  magician_female: 'קוסמת',
  mechanic_female: 'מכונאית',
  medieval_queen: 'מלכת ימי הביניים',
  mera: 'מרה',
  mermaid_female: 'בת ים',
  monk_female: 'נזירה',
  mountaineer_female: 'מטפסת הרים',
  ninja_female: 'נינג׳ה',
  noir_detective_female: 'בלשית נואר',
  paladin_female: 'פלאדין (אישה)',
  pilot_female: 'טייסת',
  pirate_female: 'פיראטית',
  police_female: 'שוטרת',
  post_apocalyptic_female: 'ניצולה פוסט-אפוקליפטית',
  queen: 'מלכה',
  racer_female: 'נהגת מרוצים',
  renaissance_female: 'דמות רנסנס (אישה)',
  rockstar_female: 'רוקסטארית',
  roman_female: 'לוחמת רומית',
  ronin_female: 'רונין (אישה)',
  royal_guard_female: 'שומרת מלכותית',
  runner_female: 'רצה',
  safari_female: 'מדריכת ספארי',
  samurai_female: 'סמוראית',
  scientist_female: 'מדענית',
  she_hulk: 'שי-האלק',
  singer_female: 'זמרת',
  skater_female: 'סקייטרית',
  soccer_female: 'כוכבת כדורגל',
  space_marine_female: 'לוחמת חלל',
  spartan_female: 'לוחמת ספרטנית',
  spiderwoman: 'ספיידרוומן',
  steampunk_inventor_female: 'ממציאת סטימפאנק',
  superheroine: 'גיבורת על',
  superwoman: 'סופר וומן',
  surfer_female: 'גולשת גלים',
  swimmer_female: 'שחיינית',
  teacher_female: 'מורה',
  tennis_female: 'שחקנית טניס',
  time_traveler_female: 'נוסעת בזמן',
  treasure_hunter_female: 'ציידת אוצרות',
  vampire_female: 'ערפדית',
  viking_queen: 'מלכת הויקינגים',
  viking_woman: 'ויקינגית',
  werewolf_female: 'אשת זאב',
  witch: 'מכשפה',
  yoga_instructor: 'מדריכת יוגה',
  caricature: 'קריקטורה',
  comics: 'קומיקס',
};

function idToTitleCase(id) {
  return id
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function buildPromptForCharacter(char) {
  if (char.prompt) return char.prompt;

  const displayName = char.name_en || idToTitleCase(char.id);
  const genderText =
    char.gender === 'female'
      ? 'female'
      : char.gender === 'male'
        ? 'male'
        : 'person';

  return `${BASE_PROMPT}\nCharacter: ${displayName}\nTransform into: ${genderText} ${displayName} with accurate outfit and setting\nBody: Full-body, natural pose, face fully visible\nSetting: A realistic environment that fits ${displayName}\nLighting: Natural realistic lighting\nStyle: Raw documentary photography, 8k, hyper-realistic, f/1.8 aperture`;
}

const SOURCE_BY_ID = new Map(RAW_CHARACTERS.map((char) => [char.id, char]));

function resolveSourceCharacter(demoChar) {
  const id = String(demoChar?.id || '');
  const gender = demoChar?.gender;

  const candidates = [id];

  if (!/_male$|_female$/.test(id)) {
    if (gender === 'male') candidates.push(`${id}_male`);
    if (gender === 'female') candidates.push(`${id}_female`);
  }

  for (const candidate of candidates) {
    const found = SOURCE_BY_ID.get(candidate);
    if (found) return found;
  }

  return null;
}

export const CHARACTERS = DEMOS_CHARACTERS.map((char) => {
  const gender = GENDER_BY_DEMO_ID.get(char.id) || char.gender;
  const source = resolveSourceCharacter({ ...char, gender });

  const name_en = source?.name_en || char.name_en || idToTitleCase(char.id);
  const name_he = source?.name_he || char.name_he || HEBREW_NAME_OVERRIDES[char.id] || name_en;
  const category = source?.category || char.category;

  const enrichedChar = {
    ...char,
    name_en,
    name_he,
    category,
    gender,
    prompt: source?.prompt || char.prompt,
  };

  return {
    ...enrichedChar,
    // Local image used in the UI
    image_url: buildLocalImageUrl(enrichedChar),
    // Original remote image from charactersData, used as a style/reference image for Fal.ai
    reference_image_url: source?.image_url || char.image_url,
    prompt: buildPromptForCharacter({ ...enrichedChar, name_en }),
  };
});

export const CATEGORIES = RAW_CATEGORIES;
export const GENDERS = RAW_GENDERS;
export { BASE_PROMPT };

// Pricing structure (ILS / USD via PayPlus or Stripe respectively)
// Each credit = 1 standard generation (2K), or 0.5 ultra (4K = 2 credits).
// Lower price per credit as bundle grows — incentivises larger purchases.
// Currency: prices are NIS (authoritative — that's what UPay/PayPlus charge).
// For English UI we convert to USD at this fixed rate.
export const USD_TO_NIS = 3.7;

export const PRICING_PLANS = [
  {
    id: 'starter',
    name: 'חבילת התחלה',
    name_he: 'חבילת התחלה',
    name_en: 'Starter',
    description_he: 'התנסות ראשונה — מספיק ל-25 תמונות',
    description_en: 'First taste — enough for 25 photos',
    price: 14,           // ₪14 ≈ $3.78 — ₪0.56/credit ≈ $0.15/credit
    credits: 25,
    bonus: 0,
    totalCredits: 25,
    pricePerCredit: 14 / 25,     // ₪0.56 per credit
    popular: false,
    savings: 0,
    features: ['גישה לכל הדמויות', 'ללא סימן מים', 'איכות 2K + 4K', 'משלוח מיידי'],
  },
  {
    id: 'boost',
    name: 'חבילת בוסט',
    name_he: 'חבילת בוסט',
    name_en: 'Boost',
    description_he: '⭐ המומלץ — לאירוע פרטי קטן',
    description_en: '⭐ Most popular — for a small private event',
    price: 36,           // ₪36 ≈ $9.73 — ₪0.45/credit ≈ $0.12/credit
    credits: 75,
    bonus: 5,
    totalCredits: 80,
    pricePerCredit: 36 / 80,     // ₪0.45 per credit
    popular: true,
    savings: 20,                  // % vs starter ppc
    features: ['גישה לכל הדמויות', 'ללא סימן מים', 'איכות 2K + 4K', 'משלוח מיידי', '5 קרדיטים מתנה'],
  },
  {
    id: 'pro',
    name: 'חבילת מקצוען',
    name_he: 'חבילת מקצוען',
    name_en: 'Pro',
    description_he: 'ליוצרים פעילים ואירועי קבוצה',
    description_en: 'For active creators & group events',
    price: 84,           // ₪84 ≈ $22.70 — ₪0.37/credit ≈ $0.10/credit
    credits: 200,
    bonus: 25,
    totalCredits: 225,
    pricePerCredit: 84 / 225,    // ₪0.37 per credit
    popular: false,
    savings: 33,
    features: ['גישה לכל הדמויות', 'ללא סימן מים', 'איכות 2K + 4K', 'תמיכה עדיפה', '25 קרדיטים מתנה'],
  },
  {
    id: 'business',
    name: 'חבילת עסקים',
    name_he: 'חבילת עסקים',
    name_en: 'Business',
    description_he: 'לאירועי חברה ועסקים — חיסכון משמעותי',
    description_en: 'For company events — best per-credit price',
    price: 210,          // ₪210 ≈ $56.76 — ₪0.30/credit ≈ $0.08/credit
    credits: 600,
    bonus: 100,
    totalCredits: 700,
    pricePerCredit: 210 / 700,   // ₪0.30 per credit
    popular: false,
    savings: 46,
    features: ['גישה לכל הדמויות', 'ללא סימן מים', 'איכות 2K + 4K', 'תמיכה עדיפה 24/7', 'רישיון מסחרי', '100 קרדיטים מתנה'],
  },
  {
    id: 'enterprise',
    name: 'חבילת ארגונים',
    name_he: 'חבילת ארגונים',
    name_en: 'Enterprise',
    description_he: 'לעמדות אירועים מקצועיות — אלפי תמונות',
    description_en: 'For professional photo booth stations — thousands of photos',
    price: 650,          // ₪650 ≈ $175.68 — ₪0.26/credit ≈ $0.07/credit
    credits: 2000,
    bonus: 500,
    totalCredits: 2500,
    pricePerCredit: 650 / 2500,  // ₪0.26 per credit
    popular: false,
    savings: 54,
    features: ['גישה לכל הדמויות', 'ללא סימן מים', 'איכות 2K + 4K', 'תמיכה ייעודית', 'רישיון מסחרי מורחב', 'API Access', '500 קרדיטים מתנה'],
  }
];

// Custom-amount limits for the slider on /pricing
export const CUSTOM_CREDITS_MIN = 25;
export const CUSTOM_CREDITS_MAX = 20000;

export const getCharacterById = (id) =>
  CHARACTERS.find((char) => char.id === id);

export const getCharactersByCategory = (categoryId) =>
  CHARACTERS.filter((char) => char.category === categoryId);

// ─── "Surprise Me" ─────────────────────────────────────────────────────────
// Components import characters from this module, not from charactersData, so the
// pool is assembled here — that way a roll lands on the image-resolved character
// and the reveal can show its picture.
export { SURPRISE_ID, SURPRISE_CHARACTER, SURPRISE_ONLY_CHARACTERS } from './charactersData';

export const SURPRISE_POOL = [...RAW_SURPRISE_ONLY, ...CHARACTERS];

/** Roll a random character, avoiding `excludeId` so a re-roll actually changes. */
export const pickSurpriseCharacter = (excludeId = null) => {
  const pool = SURPRISE_POOL.filter((c) => c.id !== excludeId);
  const from = pool.length > 0 ? pool : SURPRISE_POOL;
  return from[Math.floor(Math.random() * from.length)];
};
